/**
 * #670 — organization automation wind-down (manager surface for #300).
 *
 * The design rule is TriggerRecoveryPanel's, verbatim: the SERVER decides.
 * Every verdict rendered here comes off the POST response via windDownStore;
 * nothing is synthesized client-side, nothing is persisted, and rendering
 * this panel never invokes the wind-down endpoint. A manager acts on their
 * own org (no ?orgId — the relay 403s a foreign one); an admin acts on the
 * RESOLVED selected org only, because the stored id can name a deleted org
 * (#295). Live-role gate on top of route inheritance: a session whose
 * persisted role is not manager/admin renders nothing.
 */
import { useEffect, useState } from 'react';
import { Loader2, ShieldAlert } from 'lucide-react';
import { ConfirmModal } from '../../components/atoms/Modal';
import { apiFetch, useAuthStore } from '../../stores/authStore';
import type { OrgOption } from '../../stores/orgScope';
import { useWindDownStore, type WindDownFailureClass } from '../../stores/windDownStore';
import { AutomationWindDownResultCard } from './AutomationWindDownResultCard';

const FAILURE_COPY: Record<WindDownFailureClass, { title: string; body: string; retryable: boolean }> = {
  'session-expired': {
    title: 'Your session could not be renewed.',
    body: 'Nothing was submitted — the request was rejected before the wind-down started. Reload and sign in again if this repeats.',
    retryable: false,
  },
  'not-authorized': {
    title: 'You are not authorized to wind down this organization.',
    body: 'If your role or organization changed recently, sign in again to refresh your session. Nothing was submitted.',
    retryable: false,
  },
  'already-running': {
    title: 'A wind-down for this organization is already running on this relay.',
    body: 'Wait for it to finish, then retry to run — and re-verify — again.',
    retryable: true,
  },
  'org-not-found': {
    title: 'That organization no longer exists.',
    body: 'It may have been deleted since it was selected. Re-select an organization in the toolbar.',
    retryable: false,
  },
  'endpoint-missing': {
    title: 'The server does not expose the wind-down endpoint.',
    body: 'The relay and this UI are probably different versions. Nothing ran.',
    retryable: false,
  },
  'gateway-unavailable': {
    title: 'The connection to the server timed out — the wind-down is probably still running.',
    body: 'Do not assume anything about the book. Wait, then retry to re-verify; a retry while it is still running is refused by the server.',
    retryable: true,
  },
  'request-rejected': {
    title: 'The server rejected the request.',
    body: 'Nothing ran. Re-select the organization and try again.',
    retryable: true,
  },
  'server-error': {
    title: 'The wind-down did not run to completion.',
    body: 'Retrying re-runs the full wind-down: it only ever touches orders in the automation namespaces and re-verifies at the venue — but it is a second destructive run, not a status check.',
    retryable: true,
  },
  network: {
    title: 'The request did not complete.',
    body: 'The network dropped mid-request, but the wind-down may still be running on the server. Wait a moment, then retry to re-verify; a retry while it is still running is refused by the server.',
    retryable: true,
  },
  'unknown-response': {
    title: 'The server returned a response this build cannot interpret.',
    body: 'Do NOT assume automation is stopped. Confirm the relay and this UI are the same version, then retry to re-verify.',
    retryable: true,
  },
};

export function AutomationWindDownPanel({
  adminMode,
  selectedOrg,
  orgsLoaded = true,
  orgsError = null,
  onRetryOrgs,
  className = 'mb-6',
}: {
  adminMode?: boolean;
  selectedOrg: OrgOption | null;
  orgsLoaded?: boolean;
  /** Set when the admin org list failed to load — shown in place of "Loading…". */
  orgsError?: string | null;
  onRetryOrgs?: () => void;
  className?: string;
}) {
  const role = useAuthStore((s) => s.user?.role);

  // Manager's own org, resolved by name so the confirmation can NAME the
  // organization (#670 product contract). Fail-closed: no resolved name, no
  // arm button. Admins use the resolved selectedOrg and skip this read.
  // Inline async IIFE on purpose — extracting it makes set-state-in-effect
  // fire (see index.tsx:212-218), and the budget has zero slack.
  const [ownOrg, setOwnOrg] = useState<OrgOption | null>(null);
  const isManager = role === 'manager';
  useEffect(() => {
    if (!isManager) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch('/api/manager/org');
        const j = (await res.json().catch(() => ({}))) as { org?: { id?: unknown; name?: unknown } };
        if (!cancelled && res.ok && typeof j.org?.id === 'string' && typeof j.org?.name === 'string') {
          setOwnOrg({ id: j.org.id, name: j.org.name });
        }
      } catch {
        /* name stays unresolved; arm stays disabled */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isManager]);

  const targetOrg = adminMode ? selectedOrg : ownOrg;
  const attempt = useWindDownStore((s) => (targetOrg ? s.attemptsByOrg[targetOrg.id] : undefined));
  const busy = attempt?.kind === 'running';

  const run = useWindDownStore((s) => s.run);
  const lastResult = useWindDownStore((s) => (targetOrg ? s.lastResultByOrg[targetOrg.id] : undefined));
  const [confirming, setConfirming] = useState<OrgOption | null>(null);

  // Warn before the tab closes while a wind-down is in flight: the response
  // (residue IDs + audit ID) is only deliverable to this session.
  useEffect(() => {
    if (!busy) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [busy]);

  if (role !== 'manager' && role !== 'admin') return null;

  return (
    <section className={`bg-card border-border rounded-lg border p-4 ${className}`} data-density-id="winddown-panel">
      <div className="flex items-center gap-2">
        <ShieldAlert size={16} className="text-muted-foreground" />
        <h3 className="text-sm font-semibold">Automation wind-down</h3>
      </div>
      <p className="text-muted-foreground mt-1 text-[11px]">
        Disarms this organization&apos;s supported automation and cancels its resting orders at
        the venue, then verifies what remains. Covers <strong>Marty / vanilla day automation</strong>{' '}
        and <strong>Server-side trigger runs</strong>. Oscar, one-click, and manual orders are NOT
        included. Inventory is not sold.
      </p>

      {adminMode && !orgsLoaded && orgsError ? (
        <p className="text-muted-foreground mt-3 text-xs">
          Couldn&apos;t load organizations.{' '}
          {onRetryOrgs && (
            <button type="button" onClick={onRetryOrgs} className="underline underline-offset-2">Retry</button>
          )}
        </p>
      ) : adminMode && !orgsLoaded ? (
        <p className="text-muted-foreground mt-3 text-xs">Loading organizations…</p>
      ) : adminMode && !targetOrg ? (
        <p className="text-muted-foreground mt-3 text-xs">
          Select an organization in the toolbar above to enable wind-down.
        </p>
      ) : (
        <div className="mt-3 flex items-center gap-3">
          <button
            type="button"
            disabled={!targetOrg || busy}
            onClick={() => { if (targetOrg && !busy) setConfirming(targetOrg); }}
            className="bg-destructive/15 text-destructive rounded px-3 py-1.5 text-xs font-bold disabled:cursor-not-allowed disabled:opacity-50"
          >
            Wind down automation…
          </button>
          <span className="text-muted-foreground text-xs">
            Target: {targetOrg ? <strong>{targetOrg.name}</strong> : <em>resolving organization…</em>}
          </span>
        </div>
      )}
      {busy && (
        <div className="mt-3 flex items-center gap-2 text-xs" data-density-id="winddown-running">
          <Loader2 size={14} className="animate-spin" />
          <span>
            Winding down <strong>{attempt.kind === 'running' ? attempt.target.name : ''}</strong> — this
            can take a minute or more while resting orders are cancelled and the venue is re-read.
            Leave this page open; closing it loses the report.
          </span>
        </div>
      )}
      {busy && lastResult && (
        <AutomationWindDownResultCard result={lastResult.result} targetName={lastResult.target.name} previous />
      )}

      {attempt?.kind === 'result' && (
        <>
          <AutomationWindDownResultCard result={attempt.result} targetName={attempt.target.name} />
          {attempt.result.status !== 'complete' && (
            <button
              type="button"
              onClick={() => setConfirming(attempt.target)}
              className="bg-primary text-primary-foreground mt-2 rounded px-3 py-1.5 text-xs font-medium"
            >
              Retry wind-down…
            </button>
          )}
        </>
      )}

      {attempt?.kind === 'failure' && (
        <div className="mt-3" data-density-id="winddown-failure">
          <div className="rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400">
            <div className="font-semibold">{FAILURE_COPY[attempt.failure].title}</div>
            <div className="mt-0.5">{FAILURE_COPY[attempt.failure].body}</div>
            {attempt.serverMessage && (
              <div className="text-muted-foreground mt-0.5 text-[11px]">Server: {attempt.serverMessage}</div>
            )}
          </div>
          {FAILURE_COPY[attempt.failure].retryable && (
            <button
              type="button"
              onClick={() => setConfirming(attempt.target)}
              className="bg-primary text-primary-foreground mt-2 rounded px-3 py-1.5 text-xs font-medium"
            >
              Retry wind-down…
            </button>
          )}
          {lastResult && (
            <AutomationWindDownResultCard result={lastResult.result} targetName={lastResult.target.name} previous />
          )}
        </div>
      )}

      {/* Why this dodges the rev-1 clobber entirely: the modal's synchronous
          `onConfirm(); onClose();` touches two DIFFERENT pieces of state —
          run() writes the store, onClose clears only the local confirming
          flag — so there is nothing for the stale closure to clobber. */}
      <ConfirmModal
        isOpen={confirming !== null}
        onClose={() => setConfirming(null)}
        onConfirm={() => {
          if (confirming) void run(confirming, { admin: !!adminMode });
        }}
        title="Wind down organization automation?"
        message={
          confirming
            ? `Wind down ALL supported automation for "${confirming.name}"? This disarms Marty / vanilla day automation and server-side trigger runs, and cancels their resting orders at the venue. Inventory is NOT sold. Oscar, one-click, and manual orders are untouched.`
            : ''
        }
        confirmLabel="Wind down"
        variant="danger"
      />
      {/* Result/failure rendering lands in Task 5. */}
    </section>
  );
}
